import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Music2, Zap, BookOpen, ArrowRight, LogIn, Play, Check, Crown, Star } from 'lucide-react';
import ReferenceChart from '../components/ReferenceChart';
import useAuthStore from '../store/useAuthStore';

const Home = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuthStore();

  const handleStartPractice = () => {
    if (user?.has_access) {
      navigate('/practice');
    } else if (token) {
      navigate('/subscribe');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen noise-bg" data-testid="home-page">
      {/* Top Nav */}
      <div className="absolute top-0 right-0 p-4 z-10">
        {user ? (
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/subscribe')}>
              Pricing
            </Button>
            <span className="text-sm text-muted-foreground hidden md:inline">
              {user.email}
            </span>
            {user.has_access && (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="dashboard-nav-btn">
                  Dashboard
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate('/progress')} data-testid="progress-nav-btn">
                  My Progress
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate('/admin/codes')}>
                  Access Codes
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/account')}>
              My Account
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>
              Sign Out
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/subscribe')} data-testid="pricing-btn">
              Pricing
            </Button>
            <Button variant="outline" onClick={() => navigate('/auth')} data-testid="signin-btn">
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          </div>
        )}
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background image with overlay */}
        <div 
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1708395259847-59e3bc3a8cf9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwaWFubyUyMGtleXMlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc3MDEzNDc0MXww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />

        <div className="relative container mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-12">
            {/* Left content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-xl"
            >
              <div className="flex items-center gap-2 mb-6">
                <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
                  <Music2 className="w-5 h-5 text-white" />
                </div>
                <span className="text-sm font-medium text-muted-foreground uppercase tracking-widest">
                  Number System
                </span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
                Master the{' '}
                <span className="text-accent">Number</span>{' '}
                System
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Learn to think in numbers, not chord names. Build fluency in all 12 keys 
                with fast-paced flashcard drills and instant feedback.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  onClick={handleStartPractice}
                  className="btn-press bg-accent hover:bg-accent/90 text-white rounded-xl px-8"
                  data-testid="start-practice-btn"
                >
                  {user?.has_access ? 'Start Practice' : 'Get Started'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                {!user?.has_access && (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/quick-practice')}
                    className="btn-press rounded-xl px-8"
                    data-testid="quick-practice-btn"
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Try Free
                  </Button>
                )}
                <ReferenceChart />
              </div>

              {/* Video Demo Section */}
              <div className="mt-10 w-full max-w-2xl" data-testid="video-section">
                <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.2)]">
                  <iframe
                    src="https://player.vimeo.com/video/1175622792?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                    className="absolute top-0 left-0 w-full h-full"
                    title="Number System Trainer Demo"
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-3 text-center">
                  Watch how the Number System Trainer works
                </p>
              </div>
            </motion.div>

            {/* Right content - Feature cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid gap-4 w-full md:w-auto"
            >
              {[
                {
                  icon: Zap,
                  title: 'Quick Sessions',
                  desc: 'Practice in 2-5 minute bursts'
                },
                {
                  icon: Music2,
                  title: 'All 12 Keys',
                  desc: 'Master every major key'
                },
                {
                  icon: BookOpen,
                  title: 'Two Practice Modes',
                  desc: 'Number→Chord & Chord→Number'
                }
              ].map((feature, i) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-card border-2 border-border rounded-2xl brutalist-shadow"
                  data-testid={`feature-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Quick Theory Section */}
      <div className="container mx-auto px-6 md:px-12 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-card border-2 border-border rounded-3xl p-8 md:p-12 brutalist-shadow"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-foreground">
            What is the Number System?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The Number System is a method of transcribing music by 
                denoting the scale degree on which a chord is built. Instead of 
                writing chord names, you write numbers.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                For example, in the key of C: <span className="font-mono font-bold">C = 1, F = 4, G = 5</span>. 
                In G: <span className="font-mono font-bold">G = 1, C = 4, D = 5</span>. 
                Same numbers, different chords!
              </p>
            </div>
            <div className="bg-secondary/50 rounded-2xl p-6">
              <h3 className="font-semibold text-foreground mb-4">The Pattern:</h3>
              <div className="space-y-2 font-mono">
                <div className="flex gap-4">
                  <span className="w-8 text-accent font-bold">1</span>
                  <span className="text-muted-foreground">Major (tonic)</span>
                </div>
                <div className="flex gap-4">
                  <span className="w-8 text-muted-foreground">2</span>
                  <span className="text-muted-foreground">minor</span>
                </div>
                <div className="flex gap-4">
                  <span className="w-8 text-muted-foreground">3</span>
                  <span className="text-muted-foreground">minor</span>
                </div>
                <div className="flex gap-4">
                  <span className="w-8 text-accent font-bold">4</span>
                  <span className="text-muted-foreground">Major (subdominant)</span>
                </div>
                <div className="flex gap-4">
                  <span className="w-8 text-accent font-bold">5</span>
                  <span className="text-muted-foreground">Major (dominant)</span>
                </div>
                <div className="flex gap-4">
                  <span className="w-8 text-muted-foreground">6</span>
                  <span className="text-muted-foreground">minor</span>
                </div>
                <div className="flex gap-4">
                  <span className="w-8 text-muted-foreground">7</span>
                  <span className="text-muted-foreground">diminished</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Pricing Options Section */}
      <div className="container mx-auto px-6 md:px-12 py-16" data-testid="pricing-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Choose Your Path</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">Two ways to access the Number System practice tool</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Standalone Plan */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-card border-2 border-border rounded-3xl p-8 brutalist-shadow flex flex-col"
            data-testid="pricing-standalone"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                <Star className="w-5 h-5 text-accent" />
              </div>
              <h3 className="text-xl font-bold">Practice Tool Only</h3>
            </div>
            <div className="mb-4">
              <span className="text-4xl font-bold font-mono">$5</span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {['Unlimited flashcard practice', 'All 12 major keys', 'AI-powered hints', 'Progress tracking & weekly goals', 'Cancel anytime'].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => navigate(user ? '/subscribe' : '/auth')}
              className="w-full bg-accent hover:bg-accent/90 text-white rounded-xl"
              data-testid="pricing-standalone-btn"
            >
              Get Started
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>

          {/* Gospel Pattern Secrets Bundle */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-card border-2 border-foreground rounded-3xl p-8 brutalist-shadow flex flex-col relative"
            data-testid="pricing-membership"
          >
            <div className="absolute -top-3 right-6 bg-foreground text-background text-xs font-bold px-3 py-1 rounded-full">
              BEST VALUE
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-foreground/10 flex items-center justify-center">
                <Crown className="w-5 h-5 text-foreground" />
              </div>
              <h3 className="text-xl font-bold">Gospel Pattern Secrets</h3>
            </div>
            <div className="mb-4">
              <span className="text-sm text-muted-foreground">Full membership — includes everything</span>
            </div>
            <ul className="space-y-3 mb-8 flex-1">
              {[
                'Everything in the Practice Tool',
                'Complete Gospel Pattern Secrets course',
                'Video lessons & tutorials',
                'Member community access',
                'Number System Trainer included free'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-foreground flex-shrink-0 mt-0.5" />
                  <span className={i === 4 ? 'font-semibold' : ''}>{item}</span>
                </li>
              ))}
            </ul>
            <a
              href="https://gospelpatternsecrets.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Button
                className="w-full bg-foreground hover:bg-foreground/90 text-background rounded-xl"
                data-testid="pricing-membership-btn"
              >
                Join Gospel Pattern Secrets
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container mx-auto px-6 md:px-12 py-16" data-testid="faq-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-card border-2 border-border rounded-3xl p-8 md:p-12 brutalist-shadow"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-foreground">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="q1" data-testid="faq-item-1">
              <AccordionTrigger className="text-left text-base font-semibold">
                What is the Number System?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                The Number System is a method used by musicians to identify chords by their scale degree rather than their letter name. Instead of saying "C, F, G" in the key of C, you say "1, 4, 5." This makes it easy to transpose songs to any key instantly — the numbers stay the same no matter what key you're in.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q2" data-testid="faq-item-2">
              <AccordionTrigger className="text-left text-base font-semibold">
                Do I need to know how to play piano?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                A basic understanding of chords and keys is helpful, but you don't need to be an advanced player. This app is designed for beginner to intermediate piano players who want to learn the Number System. If you know your major scales and basic chord names, you're ready to start.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q3" data-testid="faq-item-3">
              <AccordionTrigger className="text-left text-base font-semibold">
                How much does it cost?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Full access to all practice modes and progress tracking is just $5 per month. You can also use a free access code if you've received one through a promotion or bonus offer.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q4" data-testid="faq-item-4">
              <AccordionTrigger className="text-left text-base font-semibold">
                Can I cancel anytime?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                Yes, absolutely. You can cancel your subscription at any time from your Account page. There are no contracts or cancellation fees — simply cancel when you're ready and you won't be charged again.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-8" data-testid="footer">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Music2 className="w-5 h-5 text-accent" />
              <span className="font-semibold">Number System Trainer</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Built for piano players who want to think in numbers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
